import "react-responsive-carousel/lib/styles/carousel.min.css";

import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactNode, useMemo, useState } from "react";
import { Control, useWatch } from "react-hook-form";

import APAccordion from "../../components/APAccordion";
import APModal from "../../components/APModal";
import APTabs from "../../components/APTabs";
import LoadingBackdrop from "../../components/LoadingBackdrop";
import TermsButton from "../../components/TermsButton";
import { toDollars } from "../../helpers/currencies";
import { downloadFileFromUrl } from "../../helpers/download";
import { PersonaliseQuoteInputs } from "../../helpers/quotes";
import { mergeAndSortVehicleOptions } from "../../helpers/vehicle";
import { useApi } from "../../providers/ApiProvider";
import { PersonalisedQuoteVehicleInfo } from "../../types/Quote";
import {
  calculateWeeklyVehicleOptionCost,
  VehicleOptions,
} from "../../types/Vehicles";
import VehicleOptionCard from "./VehicleOptionCard";
import VehicleOptionDetails from "./VehicleOptionDetails";

interface VehicleOptionTabsFlags {
  isSubmitting: boolean;
  hasPricingRequests: boolean;
  isQuoteLocked: boolean;
  isVehicleConfigurationLocked: boolean;
  showTerms?: boolean;
}

interface VehicleOptionTabsProps {
  quoteVehicleInfo: PersonalisedQuoteVehicleInfo;
  savingsPercentage: number;
  term: number;
  vehicleOptions: VehicleOptions;
  control: Control<PersonaliseQuoteInputs>;
  flags: VehicleOptionTabsFlags;
}
const VehicleOptionTabs = ({
  quoteVehicleInfo,
  savingsPercentage,
  term,
  vehicleOptions,
  control,
  flags,
}: VehicleOptionTabsProps) => {
  const { fetchWithAuth } = useApi();
  const watchedOptions = useWatch({ control, name: "options" });
  const watchedInclusions = useWatch({ control, name: "inclusions" });

  const [highlightedOptionId, setHighlightedOptionId] = useState(
    null as string | null
  );

  const onHighlightClick = (optionId: string) => {
    setHighlightedOptionId(optionId);
  };

  const onCloseModal = () => {
    setHighlightedOptionId(null);
  };

  const onOptionDownload = async (url: string, filename: string) => {
    const pdfSrc = await fetchWithAuth<string>("getFile", url, {
      responseOptions: { responseType: "fileUrl" },
    });
    downloadFileFromUrl(pdfSrc, filename);
  };

  // Build Rendered option card map
  const dealerAndInclusionOptionCards: Record<string, ReactNode> = {};

  const dealerAndInclusionOptions = mergeAndSortVehicleOptions(
    Object.values(vehicleOptions.options.dealer),
    Object.values(vehicleOptions.options.inclusion)
  );
  const inclusionOptionIds = vehicleOptions.options.inclusion
    ? Object.keys(vehicleOptions.options.inclusion)
    : [];

  dealerAndInclusionOptions.forEach((option) => {
    if (
      option.available ||
      quoteVehicleInfo.catchEEquipmentIds.includes(option.catchEOptionId)
    ) {
      const sellPrice =
        quoteVehicleInfo.catchEInclusionSellPrices[option.catchEOptionId];
      const price = sellPrice ? sellPrice.incGst : option.price;

      const renderedOption = (
        <VehicleOptionCard
          id={option.id}
          catchEOptionId={option.catchEOptionId}
          label={option.name}
          price={`${toDollars(
            calculateWeeklyVehicleOptionCost(price, savingsPercentage, term),
            0
          )}/wk^`}
          imageUrl={option.imageUrl}
          control={control}
          handleHighlightClick={onHighlightClick}
          hasHighlight={option.type === "auto-ux"}
          showImage={option.type === "auto-ux"}
          isInclusion={inclusionOptionIds.includes(option.id)}
          disableCheckbox={
            (flags.hasPricingRequests && option.type !== "auto-ux") ||
            (flags.isVehicleConfigurationLocked && option.type !== "auto-ux") ||
            flags.isQuoteLocked
          }
        />
      );

      dealerAndInclusionOptionCards[option.id] = renderedOption;
    }
  });

  const selectedOptionIds = dealerAndInclusionOptions
    .filter((option) => {
      if (!dealerAndInclusionOptionCards[option.id]) {
        return false;
      }

      const isInclusion = inclusionOptionIds.includes(option.id);
      const isSelected = isInclusion
        ? watchedInclusions?.[option.catchEOptionId]
        : watchedOptions?.[option.catchEOptionId];

      return isSelected === true;
    })
    .map((option) => option.id);

  const categoryTabs = vehicleOptions.categories.map((category) => {
    return {
      id: `category-${category.id}`,
      label: category.name,
      children: (
        <Stack spacing={1}>
          {Object.keys(dealerAndInclusionOptionCards).map((optionId) => {
            const isHidden = category.optionIds.indexOf(optionId) === -1;

            return (
              <Box
                sx={{ display: isHidden ? "none" : "initial" }}
                key={optionId}
              >
                {dealerAndInclusionOptionCards[optionId]}
              </Box>
            );
          })}
          {flags.showTerms && <TermsButton termsId="vehicleOption" />}
        </Stack>
      ),
    };
  });

  const tabs = [
    ...categoryTabs,
    ...(selectedOptionIds.length > 0
      ? [
          {
            id: "selected-vehicle-options",
            ariaLabel: `Selected options (${selectedOptionIds.length})`,
            label: (
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                <CheckIcon fontSize="small" />
                <Box component="span" sx={{ ml: 0.5 }}>
                  ({selectedOptionIds.length})
                </Box>
              </Box>
            ),
            children: (
              <Stack spacing={1}>
                {selectedOptionIds.map((optionId) => (
                  <Box key={optionId}>
                    {dealerAndInclusionOptionCards[optionId]}
                  </Box>
                ))}
                {flags.showTerms && <TermsButton termsId="vehicleOption" />}
              </Stack>
            ),
          },
        ]
      : []),
  ];
  const initialTabId = useMemo(() => {
    const hasSelectedOPtionTab = tabs.find(
      (tab) => tab.id === "selected-vehicle-options"
    );

    if (hasSelectedOPtionTab) {
      return hasSelectedOPtionTab.id;
    } else {
      vehicleOptions.categories[0]
        ? `category-${vehicleOptions.categories[0].id}`
        : undefined;
    }
  }, [vehicleOptions.categories, selectedOptionIds]);

  const highlightedOption =
    !!highlightedOptionId &&
    dealerAndInclusionOptions.find(
      (option) => option.id === highlightedOptionId
    );

  return (
    <Box>
      <APModal
        isOpen={!!highlightedOptionId && !!highlightedOption}
        onClose={onCloseModal}
        size="medium"
      >
        {highlightedOption && (
          <VehicleOptionDetails
            option={highlightedOption}
            savingsPercentage={savingsPercentage}
            term={term}
            handleOptionDownload={onOptionDownload}
          />
        )}
      </APModal>

      <Box>
        <APAccordion title="Vehicle Options" defaultExpanded>
          <Box sx={{ my: 2 }}>
            <APTabs tabs={tabs} initialTabId={initialTabId} />
          </Box>
        </APAccordion>
      </Box>

      {flags.isSubmitting && <LoadingBackdrop />}
    </Box>
  );
};

export default VehicleOptionTabs;
